/* -----------------Globals--------------- */
import { useCallback, useEffect, useState } from "react";
import { BackHandler } from "react-native";
import { Surface } from "react-native-paper";

import DialogSession from "./SessionDialog";
import SessionEndDialog from "./SessionEndDialog";
/* -----------------Child components--------------- */
import StudySessionAppBar from "./StudySessionAppBar";

import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
/* -----------------Helpers & Hooks--------------- */
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useAilaStore } from "../hooks/useAilaStore";
import { useProethicaSessionStore } from "../hooks/useProethicaSessionStore";
import { useProethicaSessionTimeStore } from "../hooks/useProethicaSessionTimeStore";
import useSessionStore from "../hooks/useSessionStore";
import useCountDown from "../hooks/useTimer";

/* -----------------Constants & Types--------------- */
import { events } from "@constants/tracking";

import { isAndroid } from "@helpers/platform";
/* -----------------Utils--------------- */
import { trackCustomEvent } from "analytics";
import { getPauseActivitiesLeft } from "../helpers/pause-dialog";
import { recordSummativeTestEvents } from "../relay/RecordSummativeTestEvents";

import { ProethicaPrinciplesDialog } from "@features/proethica-principles";
import useClerkUser from "@hooks/useClerkUser";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
/* -----------------UI Components--------------- */
import { Box, Button } from "@memorang/ui";
import { getSessionConfig } from "../containers/StudySessionBottombarContainer/helpers";
import { useSession } from "../hooks/useSession";

const TIMEOUT_DURATION_AUTO_REDIRECT = 2000;
export const StudySessionAppBarContainer = () => {
	const {
		numericId: sessionNumericId,
		id: sessionId,
		sessionTime,
		sessionItems,
		currentBlockIndex,
		inReviewMode,
		isSummativeTest,
		totalTime,
		updateTimeElapsed,
		timeElapsed,
		blockStates,
		examName,
		answers,
		startTime,
		serverExamBreak,
		itemStates,
		moduleId,
		proethicaTestKind,
	} = useSessionStore((state) => state);

	// Live ProEthica module sessions (not tests) tick the wall clock so the
	// content-only module report has a fallback time; tests keep their existing
	// timer path via isSummativeTest.
	const isProethicaModuleSession = Boolean(moduleId) && !proethicaTestKind;

	const { userPublicMetadataId } = useClerkUser();

	const { endCurrentSession } = useSession();

	const [showTimeExpiredDialog, setShowTimeExpiredDialog] = useState(false);

	const toggleShowTimeExpiredDialog = useCallback(() => {
		setShowTimeExpiredDialog((prev) => !prev);
	}, []);

	const isOlxSession = examName?.includes("OLX");

	const [hasFocus, setHasFocus] = useState(false);

	const breaks = useSessionStore((state) => state.breaks);

	const isDiagnostic = useSessionStore((state) => state.isDiagnostic);

	const lastBreak = breaks?.length
		? breaks[breaks.length - 1]
		: getSessionConfig(serverExamBreak);

	const breakTimeTakenInSeconds = lastBreak?.totalBreakTimeTakenInSeconds;

	const { isMobile } = useDevice();
	const hasAnsweredAtLeastOneItem = answers ? answers.size > 0 : false;
	const isBreakOngoing = useSessionStore((state) => state.isBreakOngoing);

	const calculateTotalTimeRemaining = () => {
		if (isOlxSession && startTime && !isBreakOngoing) {
			const currentTime = Date.now();
			let timeElapsed = Math.floor(
				(currentTime - new Date(startTime).getTime()) / 1000,
			);
			if (breakTimeTakenInSeconds) {
				timeElapsed -= breakTimeTakenInSeconds;
			}

			return Math.max(0, totalTime - timeElapsed);
		}
		return totalTime - (sessionTime || 0);
	};

	const totalTimeRemaining = calculateTotalTimeRemaining();

	const isTimeExpired = totalTimeRemaining <= 0;

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		if (isOlxSession && isTimeExpired) {
			toggleShowTimeExpiredDialog();
			timeoutId = setTimeout(() => {
				toggleShowTimeExpiredDialog();
				endCurrentSession({
					id: sessionId,
					userId: userPublicMetadataId!,
				});
				router.replace({
					pathname: "/(protected)/session/[id]/report",
					params: {
						id: sessionNumericId,
					},
				});
			}, TIMEOUT_DURATION_AUTO_REDIRECT);
		}
		return () => {
			if (timeoutId) {
				// biome-ignore lint/suspicious/noExplicitAny: NodeJS Timeout conversion for setTimeout
				clearTimeout(timeoutId as any);
			}
		};
	}, [
		endCurrentSession,
		isOlxSession,
		isTimeExpired,
		sessionId,
		sessionNumericId,
		toggleShowTimeExpiredDialog,
		userPublicMetadataId,
	]);

	useFocusEffect(
		useCallback(() => {
			setHasFocus(true);
			return () => {
				setHasFocus(false);
			};
		}, []),
	);

	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
	const [showEndReviewDialog, setShowEndReviewDialog] = useState(false);
	const [showSummativeEndSessionDialog, setShowSummativeEndSessionDialog] =
		useState(false);
	const [showPrinciplesDialog, setShowPrinciplesDialog] = useState(false);
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const updateShowDrawer = useAilaStore((state) => state.updateShowDrawer);

	const toggleShowSummativeEndSessionDialog = () => {
		setShowSummativeEndSessionDialog((prev) => !prev);
	};

	const toggeShowEndSessionDialog = useCallback(() => {
		if (inReviewMode) {
			setShowEndReviewDialog(true);
			return true;
		}
		setShowEndSessionDialog((prev) => !prev);
		return true;
	}, [inReviewMode]);

	useEffect(() => {
		if (isAndroid && hasFocus) {
			BackHandler.addEventListener(
				"hardwareBackPress",
				toggeShowEndSessionDialog,
			);

			return () =>
				// biome-ignore lint/suspicious/noExplicitAny: removeEventListener might not be present on some versions of react-native BackHandlerStatic
				(BackHandler as any).removeEventListener(
					"hardwareBackPress",
					toggeShowEndSessionDialog,
				);
		}
	}, [toggeShowEndSessionDialog, hasFocus]);

	// useEffect(() => {
	// 	if (!isSummativeTest && isWeb) {
	// 		window.onbeforeunload = () => {
	// 			return "";
	// 		};

	// 		return () => {
	// 			window.onbeforeunload = null;
	// 		};
	// 	}
	// }, [isSummativeTest]);

	const { index } = useLocalSearchParams<{
		index: string;
	}>();

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const numItemsLeft = getPauseActivitiesLeft(
		sessionItems.length,
		currentItemIndex,
	);

	const currentItem = sessionItems[currentItemIndex];
	const currentItemId = currentItem?.id;

	const currentItemState = itemStates?.get(currentItemId!);

	const isAnswered = currentItemState?.answered;

	const type = currentItem.metadata?.type;
	const currentBlockState = blockStates.get(
		sessionItems[currentBlockIndex].id!,
	);
	const currentBlockProgress = currentBlockState?.currentProgress || 0;

	const { isNegative, hours, minutes, seconds } = useCountDown(
		(totalTimeRemaining || 0) * 1000,
		isSummativeTest && !inReviewMode && !isBreakOngoing,
	);

	const formattedTime = `${
		isNegative ? "-" : ""
	}${hours}:${minutes}:${seconds}`;

	const handleUpdateItemStateForTime = useCallback(async () => {
		trackCustomEvent({
			eventName: events.sessionPaused,
		});
		const itemState = {
			sessionId: `${sessionId}`,
			sessionTime: timeElapsed + (sessionTime || 0),
			itemId: `${currentItemId}`,
			userId: String(userPublicMetadataId),
			variant: type!,
			progress: currentBlockProgress,
		};
		await recordSummativeTestEvents(itemState);
		updateShowDrawer(false);
		useProethicaSessionStore
			.getState()
			.setResumeSessionIndex(sessionNumericId, currentItemIndex);
		router.replace("/home");
	}, [
		currentBlockProgress,
		currentItemId,
		currentItemIndex,
		sessionId,
		sessionNumericId,
		sessionTime,
		timeElapsed,
		updateShowDrawer,
		userPublicMetadataId,
		type,
	]);

	useEffect(() => {
		let interval: ReturnType<typeof setInterval> | null = null;
		interval = setInterval(() => {
			if (
				!inReviewMode &&
				(isSummativeTest || isProethicaModuleSession) &&
				!isBreakOngoing
			) {
				updateTimeElapsed(timeElapsed + 1);
			}
		}, 1000);
		if (isBreakOngoing) {
			// biome-ignore lint/suspicious/noExplicitAny: NodeJS Interval conversion
			clearInterval(interval as any);
		}
		return () => {
			if (interval) {
				// biome-ignore lint/suspicious/noExplicitAny: NodeJS Interval conversion
				clearInterval(interval as any);
			}
		};
	}, [
		inReviewMode,
		isSummativeTest,
		isProethicaModuleSession,
		timeElapsed,
		updateTimeElapsed,
		isBreakOngoing,
	]);

	// Persist the module wall clock on a throttle so a same-device refresh of the
	// report (which reads the non-persisted store) still shows the tracked time.
	// Session end writes the final value in endCurrentSession.
	useEffect(() => {
		if (
			isProethicaModuleSession &&
			!inReviewMode &&
			timeElapsed > 0 &&
			timeElapsed % 10 === 0
		) {
			useProethicaSessionTimeStore
				.getState()
				.saveSessionElapsed(sessionId, sessionNumericId, timeElapsed);
		}
	}, [
		isProethicaModuleSession,
		inReviewMode,
		timeElapsed,
		sessionNumericId,
		sessionId,
	]);

	// Leaving a session early is always a pause: the session stays ACTIVE on the
	// backend and only the local resume position is saved (CMS-1538).
	const handleNavigation = (type: "positive" | "negative") => {
		toggeShowEndSessionDialog();

		if (type === "positive") {
			updateShowDrawer(false);
			useProethicaSessionStore
				.getState()
				.setResumeSessionIndex(sessionNumericId, currentItemIndex);
			router.replace("/home");
		}
	};
	const handleClickRightCta = () => {
		if (isSummativeTest) {
			if (inReviewMode) {
				setShowEndReviewDialog(true);
			} else {
				toggleShowSummativeEndSessionDialog();
			}
		} else {
			toggeShowEndSessionDialog();
		}
	};

	const handleClickButton = (type: string) => {
		if (type === "confirm") {
			handleUpdateItemStateForTime();
		}
		toggleShowSummativeEndSessionDialog();
	};

	const toggleShowEndReviewDialog = () => {
		setShowEndReviewDialog((prev) => !prev);
	};

	const handleEndReview = (type: string) => {
		if (type === "confirm") {
			updateShowDrawer(false);
			if (router.canGoBack()) {
				router.back();
			} else {
				router.replace("/home");
			}
		}
		setShowEndReviewDialog(false);
	};

	const theme = useAppTheme();

	const showToolbar =
		!(inReviewMode || isMobile) &&
		isSummativeTest &&
		demoExamId === "proethica";

	return (
		<>
			<Box>
				<StudySessionAppBar
					numericId={sessionNumericId}
					examName={examName}
					timeRemaining={`${formattedTime}`}
					isTimerExpired={isNegative}
					handleClickRightCta={handleClickRightCta}
					showPause={!isOlxSession}
					isAnswered={isAnswered}
					onPause={handleClickRightCta}
				/>
				{showToolbar && (
					<Surface
						style={{
							backgroundColor: theme.colors.surface,
							elevation: 0,
							borderBottomWidth: 1,
							borderBottomColor: theme.colors.divider,
						}}
					>
						<Box
							flexDirection="row"
							gap={12}
							paddingHorizontal={16}
							paddingVertical={8}
							alignItems="center"
						>
							<Button
								textColor={theme.colors.onSurface}
								icon="book-open-page-variant-outline"
								onPress={() => setShowPrinciplesDialog(true)}
							>
								Principles
							</Button>
						</Box>
					</Surface>
				)}
			</Box>
			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggeShowEndSessionDialog}
					handleAction={handleNavigation}
					showProgressMessage={!isDiagnostic || hasAnsweredAtLeastOneItem}
					isDiagnostic={isDiagnostic}
					numItemsLeft={numItemsLeft}
				/>
			)}
			{showSummativeEndSessionDialog && (
				<DialogSession
					type="pause"
					open={showSummativeEndSessionDialog}
					handleClose={toggleShowSummativeEndSessionDialog}
					handleClickButton={handleClickButton}
				/>
			)}
			{showEndReviewDialog && (
				<DialogSession
					type="endReview"
					maxWidth={"xs"}
					open={showEndReviewDialog}
					handleClose={toggleShowEndReviewDialog}
					handleClickButton={handleEndReview}
				/>
			)}
			{showTimeExpiredDialog && (
				<DialogSession
					type="timerExpired"
					maxWidth={"xs"}
					open={showTimeExpiredDialog}
					showCloseButton={false}
					handleClose={toggleShowTimeExpiredDialog}
				/>
			)}
			<ProethicaPrinciplesDialog
				visible={showPrinciplesDialog}
				handleClose={() => setShowPrinciplesDialog(false)}
			/>
		</>
	);
};
